import { Copy, CopyCheckIcon } from 'lucide-react';
import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import toast from 'react-hot-toast';

import { LoadingButton } from './loading-button';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { DialogContent } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';

import { useAuth } from '@/hooks/useAuth';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
// Removed broken imports - components no longer exist
import { PaymentsService } from '@/services/Payments';
import {
  PaymentCreateResponseType,
  PaymentPayloadType,
} from '@/types/payment.type';

type CreatePaymentLinkDialogProps = {
  setIsOpen: Dispatch<SetStateAction<boolean>>;
};

export function CreatePaymentLinkDialog(
  {
    // setIsOpen is not used in this simplified version
  }: CreatePaymentLinkDialogProps
) {
  const { user } = useAuth();
  const { copyToClipboard, isCopied } = useCopyToClipboard({});
  // Remove unused variable
  // const { userTag } = useParams();
  // const isVivoUser = userTag === 'vivo';

  const [isLoading, setLoading] = useState(false);
  const [payload, setPayload] = useState<PaymentPayloadType>({
    name: '',
    taxid: '',
    participantid: '',
    method: 'PIX_DICT',
    type: 'a-vista',
    amount: 0,
    date: new Date().toISOString(),
    amountchange: true,
    payerchange: true,
  });
  const [error, setError] = useState('');

  const [paymentResponse, setPaymentResponse] =
    useState<PaymentCreateResponseType>();

  const setPayloadValue = useCallback(
    (key: keyof PaymentPayloadType, value: any) => {
      setError('');
      setPayload(prevState => ({
        ...prevState,
        [key]: value,
      }));
    },
    []
  );

  const createPayment = useCallback(async () => {
    setLoading(true);
    try {
      if (!user?.apiKey) {
        throw new Error('API key não encontrada');
      }
      const response = await PaymentsService.create(payload, user.apiKey);

      setPaymentResponse(response);
      setLoading(false);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Erro ao criar pagamento';

      setError(errorMessage);
      toast.error(errorMessage);
      setLoading(false);
    }
  }, [payload, user?.apiKey]);

  const onCopy = useCallback(() => {
    if (!paymentResponse?.linkUrl || isCopied) return;

    copyToClipboard(paymentResponse?.linkUrl);
  }, [copyToClipboard, isCopied, paymentResponse?.linkUrl]);

  return (
    <DialogContent className='bg-card overflow-auto max-h-[calc(90vh)] py-10 max-w-2xl'>
      <div className='flex flex-col gap-6 h-full flex-1'>
        {user?.userTag === 'sendi' && (
          <div className='flex gap-4 items-center mb-4'>
            <img
              src='/images/sendi.jpg'
              alt='Sendi 2025'
              className='w-[80px] h-[80px] rounded-2xl object-cover'
            />
            <div className='grid items-center'>
              <p className='text-sm font-semibold'>SENDI 2025 BELO HORIZONTE</p>
              <p className='text-sm text-muted-foreground'>
                De pessoas para pessoas
              </p>
            </div>
          </div>
        )}

        {/* Payment Information */}
        <div className='space-y-4'>
          <div className='grid gap-2'>
            <Label htmlFor='amount'>Valor</Label>
            <Input
              id='amount'
              type='number'
              value={payload?.amount?.toString() || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setPayloadValue('amount', parseInt(e.target.value) || 0)
              }
              placeholder='Digite o valor'
            />
          </div>

          <div className='grid gap-2'>
            <Label htmlFor='method'>Método de Pagamento</Label>
            <Input
              id='method'
              value={payload?.method || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setPayloadValue('method', e.target.value)
              }
              placeholder='Ex: PIX'
            />
          </div>

          <div className='grid gap-2'>
            <Label htmlFor='type'>Tipo de Pagamento</Label>
            <Input
              id='type'
              value={payload?.type || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setPayloadValue('type', e.target.value)
              }
              placeholder='Ex: a-vista'
            />
          </div>
        </div>

        {/* User Information */}
        <div className='space-y-4'>
          <div className='grid gap-2'>
            <Label htmlFor='name'>Nome</Label>
            <Input
              id='name'
              value={payload?.name || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setPayloadValue('name', e.target.value)
              }
              placeholder='Nome do pagador'
            />
          </div>

          <div className='grid gap-2'>
            <Label htmlFor='taxid'>CPF/CNPJ</Label>
            <Input
              id='taxid'
              value={payload?.taxid || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setPayloadValue('taxid', e.target.value)
              }
              placeholder='000.000.000-00'
            />
          </div>
        </div>

        {/* Participant ID */}
        <div className='grid gap-2'>
          <Label htmlFor='participantid'>ID do Participante</Label>
          <Input
            id='participantid'
            value={payload?.participantid || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setPayloadValue('participantid', e.target.value)
            }
            placeholder='ID do banco/participante'
          />
        </div>

        <div className='grid gap-2 text-sm'>
          <p className='font-semibold'>Permitir alterações de:</p>
          <div className='flex items-center gap-2'>
            <Checkbox
              disabled={!payload?.amount}
              checked={payload?.amountchange}
              onCheckedChange={value => setPayloadValue('amountchange', value)}
            />
            <p>Valor do pagamento</p>
          </div>
          <div className='flex items-center gap-2'>
            <Checkbox
              disabled={!payload?.name || !payload?.taxid}
              checked={payload?.payerchange}
              onCheckedChange={value => setPayloadValue('payerchange', value)}
            />
            <p>Pagador</p>
          </div>
        </div>

        <div className='flex items-center gap-2'>
          <LoadingButton
            isLoading={isLoading}
            onClick={createPayment}
            disabled={
              !payload.name ||
              !payload.taxid ||
              !payload.participantid ||
              !payload.amount
            }
            className='flex-1'
            size='lg'
          >
            Criar link de pagamento
          </LoadingButton>
        </div>

        {paymentResponse?.linkUrl && (
          <div className='p-4 bg-muted/50 rounded-md'>
            <p className='font-semibold text-sm text-center mb-3'>
              Link de pagamento criado com sucesso!
            </p>
            <div className='flex items-center gap-2'>
              <input
                type='text'
                value={paymentResponse.linkUrl}
                readOnly
                className='flex-1 px-3 py-2 text-sm border rounded-md bg-background'
              />
              <Button size='sm' onClick={onCopy} className='shrink-0'>
                {isCopied ? (
                  <CopyCheckIcon className='h-4 w-4' />
                ) : (
                  <Copy className='h-4 w-4' />
                )}
              </Button>
            </div>
          </div>
        )}

        {error && <p className='text-red-500 text-sm text-center'>{error}</p>}
      </div>
    </DialogContent>
  );
}
